import { motion } from 'framer-motion';
import { Wine, ArrowRight, Info } from 'lucide-react';
import { useTastingStore } from '@/stores/tastingStore';
import { useEventStore } from '@/stores/eventStore';

export default function IntroStep() {
  const { spirits, nextStep } = useTastingStore();
  const { event } = useEventStore();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <Wine className="w-10 h-10 text-primary-500" />
        </motion.div>

        <h1 className="text-3xl font-display font-bold text-dark-50 mb-4">
          Willkommen zum Tasting
        </h1>

        <p className="text-dark-400 max-w-md mx-auto">
          Du wirst heute {spirits.length} Spirituose{spirits.length !== 1 ? 'n' : ''} verkosten.
          Nimm dir Zeit und genieße jeden Schluck.
        </p>
      </div>

      {/* Spirits Preview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <h2 className="text-lg font-semibold text-dark-100 mb-4">Heutige Verkostung</h2>
        <div className="space-y-3">
          {spirits.map((spirit, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-dark-800/50 rounded-xl"
            >
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium">
                {index + 1}
              </div>
              <div>
                <div className="font-medium text-dark-100">{spirit.spirit_name}</div>
                {spirit.spirit_brand && (
                  <div className="text-sm text-dark-400">{spirit.spirit_brand}</div>
                )}
              </div>
            </div>
          ))}

          {spirits.length === 0 && (
            <div className="text-center py-8 text-dark-500">
              <Wine className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Noch keine Spirituosen hinzugefügt</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card bg-primary-500/10 border-primary-500/30"
      >
        <div className="flex gap-4">
          <Info className="w-6 h-6 text-primary-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-dark-100 mb-2">So funktioniert's</h3>
            <ul className="text-dark-300 text-sm space-y-2">
              <li>1. <strong>Aroma:</strong> Rieche am Glas und beschreibe die Aromen</li>
              <li>2. <strong>Geschmack:</strong> Nimm einen kleinen Schluck und lass ihn auf der Zunge</li>
              <li>3. <strong>Abgang:</strong> Achte auf den Nachgeschmack</li>
              <li>4. <strong>Botanicals:</strong> Versuche die Zutaten zu erkennen</li>
              <li>5. <strong>Bewertung:</strong> Gib deine Gesamtbewertung ab</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Mode Info */}
      {event && event.tasting_mode !== 'async' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card bg-accent-500/10 border-accent-500/30"
        >
          <div className="flex gap-4">
            <Info className="w-6 h-6 text-accent-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-dark-100 mb-1">Geführtes Tasting</h3>
              <p className="text-dark-300 text-sm">
                Dieses Tasting wird vom Host geführt. Warte auf die Freigabe des nächsten Schritts.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Start Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <button
          onClick={nextStep}
          disabled={spirits.length === 0}
          className="btn-primary w-full btn-lg flex items-center justify-center gap-2"
        >
          Tasting starten
          <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>
    </div>
  );
}
